import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useState, useEffect } from 'react'
import { SearchableSelect } from './SearchableSelect'

const COUNTRY_CODES = [
  { code: 'EC', prefijo: '+593', label: 'Ecuador (+593)', ejemplo: '987654321' },
  { code: 'PE', prefijo: '+51', label: 'Perú (+51)', ejemplo: '987654321' },
  { code: 'CO', prefijo: '+57', label: 'Colombia (+57)', ejemplo: '3001234567' },
  { code: 'CL', prefijo: '+56', label: 'Chile (+56)', ejemplo: '912345678' },
  { code: 'AR', prefijo: '+54', label: 'Argentina (+54)', ejemplo: '91112345678' },
  { code: 'MX', prefijo: '+52', label: 'México (+52)', ejemplo: '1234567890' },
  { code: 'US', prefijo: '+1', label: 'EE.UU. (+1)', ejemplo: '2025551234' },
  { code: 'BR', prefijo: '+55', label: 'Brasil (+55)', ejemplo: '11987654321' },
  { code: 'UY', prefijo: '+598', label: 'Uruguay (+598)', ejemplo: '94123456' },
  { code: 'PY', prefijo: '+595', label: 'Paraguay (+595)', ejemplo: '981234567' },
  { code: 'BO', prefijo: '+591', label: 'Bolivia (+591)', ejemplo: '71234567' },
  { code: 'VE', prefijo: '+58', label: 'Venezuela (+58)', ejemplo: '4121234567' },
  { code: 'PA', prefijo: '+507', label: 'Panamá (+507)', ejemplo: '61234567' },
  { code: 'CR', prefijo: '+506', label: 'Costa Rica (+506)', ejemplo: '83121234' },
  { code: 'GT', prefijo: '+502', label: 'Guatemala (+502)', ejemplo: '51234567' },
  { code: 'HN', prefijo: '+504', label: 'Honduras (+504)', ejemplo: '91234567' },
  { code: 'SV', prefijo: '+503', label: 'El Salvador (+503)', ejemplo: '71234567' },
  { code: 'NI', prefijo: '+505', label: 'Nicaragua (+505)', ejemplo: '81234567' },
  { code: 'DO', prefijo: '+1809', label: 'Rep. Dominicana (+1809)', ejemplo: '1234567' },
  { code: 'CU', prefijo: '+53', label: 'Cuba (+53)', ejemplo: '51234567' },
  { code: 'ES', prefijo: '+34', label: 'España (+34)', ejemplo: '612345678' },
]

interface PhoneInputProps {
  value: string
  onChange: (value: string) => void
  paisCodigo?: string | null
  error?: string
  label?: string
  required?: boolean
}

export function PhoneInput({
  value,
  onChange,
  paisCodigo,
  error,
  label = 'Teléfono Celular',
  required = false,
}: PhoneInputProps) {
  // Separar prefijo y número del valor completo
  const findPrefijo = (fullValue: string) => {
    if (!fullValue) return { prefijo: '', numero: '' }
    // Buscar el prefijo más largo que coincida
    const sorted = [...COUNTRY_CODES].sort((a, b) => b.prefijo.length - a.prefijo.length)
    for (const c of sorted) {
      if (fullValue.startsWith(c.prefijo)) {
        return { prefijo: c.prefijo, numero: fullValue.slice(c.prefijo.length) }
      }
    }
    return { prefijo: '', numero: fullValue }
  }

  const parsed = findPrefijo(value)

  // Auto-seleccionar prefijo por país si no hay valor
  const [selectedPrefijo, setSelectedPrefijo] = useState(parsed.prefijo)
  const [numero, setNumero] = useState(parsed.numero)

  useEffect(() => {
    if (paisCodigo && !value) {
      const match = COUNTRY_CODES.find(c => c.code === paisCodigo)
      if (match) {
        setSelectedPrefijo(match.prefijo)
      }
    }
  }, [paisCodigo, value])

  // Sincronizar cuando el valor externo cambia (ej: edición)
  useEffect(() => {
    if (value) {
      const p = findPrefijo(value)
      setSelectedPrefijo(p.prefijo)
      setNumero(p.numero)
    }
  }, [value])

  const handlePrefijoChange = (prefijo: string) => {
    setSelectedPrefijo(prefijo)
    onChange(prefijo + numero)
  }

  const handleNumeroChange = (num: string) => {
    // Solo números
    const limpio = num.replace(/\D/g, '')
    setNumero(limpio)
    onChange(selectedPrefijo + limpio)
  }

  return (
    <div className="space-y-2">
      <Label>
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      <div className="flex gap-2">
        <div className="w-[180px] shrink-0">
          <SearchableSelect
            options={COUNTRY_CODES.map(c => ({ value: c.prefijo, label: c.label }))}
            value={selectedPrefijo}
            onValueChange={handlePrefijoChange}
            placeholder="Código"
            searchPlaceholder="Buscar país..."
          />
        </div>
        <Input
          value={numero}
          onChange={(e) => handleNumeroChange(e.target.value)}
          placeholder={COUNTRY_CODES.find(c => c.prefijo === selectedPrefijo)?.ejemplo || '987654321'}
          inputMode="numeric"
        />
      </div>
      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  )
}
